// Итоги ведомости одной цепочкой: начислено, минус три вычета, равно к выплате.
//
// ## Почему это цепочка, а не четыре числа рядом
//
// Правило появилось 25.08.2026 после живого прогона. Полоса показывала четыре величины —
// начислено, официальная часть, аванс, всего к выплате, — и на демонстрационных данных они не
// сходились: 198 064,51 при нулевой официальной части и нулевом авансе давали 194 064,51 к
// выплате. Четыре тысячи разницы объяснялись колонками справа, до которых надо было
// доскроллить восемнадцать колонок.
//
// Ряд равнозначных чисел, стоящих в строку, человек читает как формулу — особенно этот
// человек: финансовый директор заказчика проверяет ведомость ровно так, вычитанием в уме,
// 85 000 − 25 935 − 25 934 = 33 131. Формула, которая не сходится, читается не как «тут не
// всё показано», а как «программа посчитала неверно», и дальше не верят уже ни одной цифре.
//
// Поэтому вычетов ТРИ, а не два, знак действия стоит В ПОДПИСИ каждой величины, и сумма
// обязана сходиться. Знак именно в подписи, а не отдельной колонкой между числами: отдельная
// колонка не влезает в равномерную сетку величин, которой набрана такая же полоса в сводке, и
// один и тот же ряд оказался бы набран двумя способами на соседних вкладках.
//
// Крупная величина в полосе ровно одна — ответ. Начислено, вычеты и итог не равнозначны:
// человек открывает экран ради последнего числа, а первые четыре читает, только если
// последнее его удивило. Одинаковый кегль на всех пяти врал бы про важность.
//
// ## Почему «аванс удержан», а не «аванс»
//
// Аванс, превысивший начисленное, удерживается НЕ ЦЕЛИКОМ: каскад берёт сколько есть, остаток
// уходит в долг «неудержанный аванс» (сценарий 31). Полный аванс в цепочке вычетов — ложное
// слагаемое: вычли бы больше, чем на самом деле вычли. Показываем удержанное, а расхождение с
// запрошенным называем словами прямо под числом.
//
// Неудержанный аванс прошлых месяцев — тоже аванс, хоть и удерживается вместе с долгами. До
// 13.10.2026 он попадал в «прочие удержания»: у 82 человек сентября это 949 427 ₽, и финансовый
// директор заказчика не нашёл их среди авансов (12.10.2026). Он стоит здесь и назван под числом.
//
// ## Почему «прочие удержания» считаются остатком
//
// Форма, билеты, обучение, удостоверение, штрафы, кредит, квартиры — это восемь и больше
// колонок, и их состав задаётся шаблоном объекта. Складывать их здесь по кодам значило бы
// держать в вебе второй список того, что считается удержанием, и он разошёлся бы с шаблоном на
// первом же новом виде удержания. Остаток «сколько осталось после аванса минус сколько к
// выплате» верен при любом составе колонок по определению.
//
// ## Почему числа берутся из разных мест
//
// «Начислено» и «Всего к выплате» приходят ИТОГАМИ СЕРВЕРА: деньги считает он. Вычеты
// складываются здесь целыми копейками — сервер их итогом не отдаёт, а сложение целых копеек
// точное и округлений не вносит.
//
// И тут же стоит СВЕРКА: сумма итогов по людям против итога ведомости. Показывается ВСЕГДА, а
// не только при расхождении — молчание читается как «не проверяли». Расхождение здесь означает,
// что человек видит на экране одно, а в печатную форму уйдёт другое, и узнать об этом он должен
// не на встрече с заказчиком.

import type { PayrollEmployeeOut, PayrollTotalsOut } from '@/shared/api/endpoints/payroll';
import { formatKopecks } from '@/shared/money/format';
import { countLabel } from '@/shared/text/plural';
import { Metric, MetricRow } from '@/shared/ui/metric';
import { cn } from '@/utils/cn';

import { перенесённыйАванс } from './аванс';

export interface SheetTotals {
  readonly accrued: number;
  readonly official: number;
  /** Сколько аванса каскад удержал на самом деле. Вычитается из начисленного. */
  readonly advance: number;
  /** Сколько аванса просили удержать. Больше удержанного — остаток ушёл в долг. */
  readonly advanceRequested: number;
  /** Сколько из удержанного — неудержанный аванс прошлых месяцев. Входит в `advance`. */
  readonly advanceCarried: number;
  /** Всё, что каскад взял после аванса: форма, билеты, обучение, штрафы, кредит. */
  readonly deductions: number;
  readonly payout: number;
  /**
   * Сумма итогов по людям. Равна `payout`, пока сервер и экран согласны.
   *
   * `null` — сверять не с чем: список людей в ответе необязательный, и его отсутствие нельзя
   * принимать за ноль. Иначе под здоровой ведомостью загорелось бы «расхождение» на всю сумму.
   */
  readonly payoutByPeople: number | null;
  readonly employees: number;
  readonly rows: number;
  readonly excludedRows: number;
  readonly calculated: boolean;
}

export function buildTotals(
  employees: readonly PayrollEmployeeOut[] | undefined,
  totals: PayrollTotalsOut,
  calculated: boolean,
): SheetTotals {
  const люди = employees;
  let официальная = 0;
  let удержаноАванса = 0;
  let запрошеноАванса = 0;
  let перенесеноАванса = 0;
  let прочие = 0;
  let поЛюдям = 0;
  for (const человек of люди ?? []) {
    официальная += (человек.official_paid ?? 0) + (человек.official_rest ?? 0);
    запрошеноАванса += человек.advance_total;
    // Удержано ровно столько, сколько было из чего удерживать. Отрицательный остаток после
    // аванса означает, что каскад обнулил итог и перенёс разницу в долг: вычитать полный
    // аванс тогда нельзя, его целиком не удерживали.
    удержаноАванса += Math.min(человек.advance_total, Math.max(0, человек.unofficial_base));
    // Аванс прошлых месяцев каскад берёт после аванса месяца, вместе с долгами, — из остатка,
    // который иначе весь ушёл бы в «прочие». Переносится он оттуда сюда, сумма не меняется.
    const перенесено = перенесённыйАванс(человек);
    перенесеноАванса += перенесено;
    удержаноАванса += перенесено;
    прочие += Math.max(0, человек.after_advance) - человек.total_payout - перенесено;
    поЛюдям += человек.total_payout;
  }
  return {
    accrued: totals.accrued,
    official: официальная,
    advance: удержаноАванса,
    advanceRequested: запрошеноАванса,
    advanceCarried: перенесеноАванса,
    deductions: прочие,
    payout: totals.total_payout,
    payoutByPeople: люди === undefined ? null : поЛюдям,
    employees: totals.employees_count,
    rows: totals.rows_count,
    excludedRows: totals.excluded_rows,
    calculated,
  };
}

/**
 * Сходится ли цепочка вычетов.
 *
 * Не сойтись она может ровно в одном случае: официальная часть больше начисленного, а это
 * блокирующая находка панели проверки. Проверяем всё равно — молчаливо неверная арифметика на
 * этом экране дороже лишней строки кода.
 */
export function цепочкаСходится(totals: SheetTotals): boolean {
  return totals.accrued - totals.official - totals.advance - totals.deductions === totals.payout;
}

export function TotalsBar({
  totals,
  footer,
}: {
  totals: SheetTotals;
  /**
   * Подвал полосы: сколько строк, сходится ли итог, пересчёт и время среза.
   *
   * Стоит ЗДЕСЬ, а не отдельной полосой под ней. Над сеткой и так пять ярусов — разбор,
   * итоги, действие, счёт строк, пояснение про шаблон, — и каждый лишний ярус это строка
   * ведомости, которой финансовый директор не увидит. А счёт строк и сверка описывают ровно
   * те числа, что стоят выше: разрывать их границей значило говорить, что это про разное.
   */
  footer?: import('react').ReactNode;
}) {
  const известно = (значение: number) => totals.calculated || значение !== 0;
  const недоудержано = totals.advanceRequested - (totals.advance - totals.advanceCarried);
  return (
    <div className='border-b border-stroke-soft-200 px-5 py-2'>
      {/* Ряд собран ТЕМ ЖЕ примитивом, что и такая же цепочка в сводке за месяц: пока полоса
          рисовала свою «величину», два одинаковых по смыслу ряда на соседних вкладках
          отличались и кеглем, и шириной долей. Доли равные — иначе «0,00» занимает четверть
          строки, а «198 064,51» три четверти, и глазу не за что зацепиться. */}
      <MetricRow columns={5}>
        <Величина
          подпись='Начислено'
          значение={totals.accrued}
          пояснение='тарифные строки и премии, до вычетов'
          известно={известно(totals.accrued)}
        />
        <Величина
          подпись='− Официальная часть'
          значение={totals.official}
          пояснение={
            totals.official === 0
              ? 'официальный период не связан'
              : 'выплачено плюс остаток, до каскада'
          }
          известно={известно(totals.official)}
          приглушённо={totals.official === 0}
        />
        <Величина
          подпись='− Аванс удержан'
          значение={totals.advance}
          пояснение={
            недоудержано > 0
              ? `просили ${formatKopecks(totals.advanceRequested)}: остаток ушёл в долг`
              : totals.advance === 0
                ? // Не «авансов не было»: программа знает авансы только по заявкам подотчёта, а
                  // заказчик выдаёт их и переводом через переписку. Утверждать про месяц нельзя
                  // (проход по боевым данным 23.09.2026: в архиве 169 000 ₽ авансов за 12.08).
                  'через программу авансов не выдавали'
                : totals.advanceCarried === totals.advance
                  ? 'неудержанный аванс прошлых месяцев'
                  : totals.advanceCarried > 0
                    ? `из них прошлых месяцев ${formatKopecks(totals.advanceCarried)}`
                    : 'удержан первым, до прочих'
          }
          известно={известно(totals.advance)}
          приглушённо={totals.advance === 0}
        />
        <Величина
          подпись='− Прочие удержания'
          значение={totals.deductions}
          пояснение={
            totals.deductions === 0
              ? 'в этом расчёте ничего не удержано'
              : 'форма, билеты, обучение, штрафы'
          }
          известно={известно(totals.deductions)}
          приглушённо={totals.deductions === 0}
        />
        <Величина
          подпись='= Всего к выплате'
          значение={totals.payout}
          пояснение='на карту, после каскада'
          известно={известно(totals.payout)}
          крупно
        />
      </MetricRow>
      {totals.calculated && !цепочкаСходится(totals) && (
        <p className='pt-1 text-paragraph-xs text-warning-base'>
          Вычеты не сходятся с итогом: официальная часть больше начисленного. Разберите это в панели
          проверки — до утверждения такая ведомость не пройдёт.
        </p>
      )}
      {footer !== undefined && (
        <div className='mt-2 flex flex-wrap items-center justify-between gap-3 border-t border-stroke-soft-200 pt-2'>
          {footer}
        </div>
      )}
    </div>
  );
}

/**
 * Что перечислено и сколько (правило 2), и сходится ли итог.
 *
 * Стоит НАД сеткой, а не среди денежных величин: число людей — не деньги, и в одном ряду с
 * суммами оно читается как пятая сумма.
 */
export function RowsCaption({ totals }: { totals: SheetTotals }) {
  return (
    <span className='text-paragraph-xs text-text-sub-600'>
      {countLabel(totals.employees, ['человек', 'человека', 'человек'])},{' '}
      {countLabel(totals.rows, ['тарифная строка', 'тарифные строки', 'тарифных строк'])}
      {totals.excludedRows > 0 && <>, {totals.excludedRows} вне расчёта</>}
      <Сверка totals={totals} />
    </span>
  );
}

/**
 * Сходится ли итог ведомости с суммой по людям.
 *
 * Показывается ВСЕГДА, когда есть что с чем сверять: молчание читается как «не проверяли».
 * Но сверять нечего в двух случаях — список людей не приехал и расчёта ещё не было, — и бодрое
 * «сходится» про два нуля было бы такой же неправдой, как молчание.
 */
function Сверка({ totals }: { totals: SheetTotals }) {
  if (totals.payoutByPeople === null) {
    return (
      <span className='pl-2 text-text-sub-600'>
        сверить итог со строками не с чем: список людей в ответе не приехал
      </span>
    );
  }
  if (!totals.calculated) {
    return <span className='pl-2 text-text-sub-600'>сверять нечего: расчёта ещё не было</span>;
  }
  const сходится = totals.payout === totals.payoutByPeople;
  return (
    <span className={cn('pl-2', сходится ? 'text-text-sub-600' : 'text-warning-base')}>
      {сходится
        ? 'итог сходится со строками'
        : `итог ведомости ${formatKopecks(totals.payout)}, а по людям выходит ${formatKopecks(totals.payoutByPeople)} — расхождение, покажите его разработчикам`}
    </span>
  );
}

function Величина({
  подпись,
  значение,
  пояснение,
  известно,
  крупно,
  приглушённо,
}: {
  подпись: string;
  значение: number;
  пояснение: string;
  /** Ложь — расчёта не было, и ноль здесь означал бы «ничего», а не «не знаем». */
  известно: boolean;
  /** Крупная величина в полосе ровно одна: тот ответ, ради которого экран открыли. */
  крупно?: boolean;
  приглушённо?: boolean;
}) {
  return (
    <Metric
      label={подпись}
      value={известно ? formatKopecks(значение) : '—'}
      // Ноль тут — посчитанный результат, а не пустота: он остаётся числом, только тише.
      tone={
        крупно === true ? 'важный' : !известно || приглушённо === true ? 'приглушённый' : 'обычный'
      }
      // Пояснение при НЕИЗВЕСТНОЙ величине не пишется: причина одна на весь ряд, и она уже
      // названа полосой проверки прямо над ним. Пока писалось, на экране стояло шесть
      // сообщений об одном и том же — одно сверху и по одному под каждой из пяти величин.
      // Прочерк сам по себе говорит «не знаем», а «почему» человек уже прочитал.
      note={известно ? пояснение : undefined}
    />
  );
}
